// pages/tests/MonthlyTestFiles.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  IconButton,
  Chip,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Tab,
  Tabs,
  Autocomplete,
  Avatar,
  Divider,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocIcon,
  InsertDriveFile as FileIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';

const MonthlyTestFiles = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [test, setTest] = useState(null);
  const [files, setFiles] = useState([]);
  const [students, setStudents] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadType, setUploadType] = useState('general');
  const [uploadStudent, setUploadStudent] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Sample data
  const sampleTest = {
    id: 1,
    testName: 'Monthly Test - January',
    className: 'Class 10',
    sectionName: 'A',
    subjectName: 'Mathematics',
    testDate: '2024-01-15'
  };

  const sampleStudents = [
    { id: 1, rollNo: '001', name: 'John Doe' },
    { id: 2, rollNo: '002', name: 'Jane Smith' },
    { id: 3, rollNo: '003', name: 'Bob Johnson' },
    { id: 4, rollNo: '004', name: 'Alice Brown' },
    { id: 5, rollNo: '005', name: 'Charlie Wilson' },
  ];

  const sampleFiles = [
    {
      id: 1,
      fileName: 'question_paper.pdf',
      filePath: '/uploads/question_paper.pdf',
      fileType: 'application/pdf',
      fileSize: 1024000,
      uploadedAt: '2024-01-10T10:00:00Z',
      studentId: null,
      studentName: null,
      uploadType: 'general'
    },
    {
      id: 2,
      fileName: 'answer_key.pdf',
      filePath: '/uploads/answer_key.pdf',
      fileType: 'application/pdf',
      fileSize: 512000,
      uploadedAt: '2024-01-10T10:30:00Z',
      studentId: null,
      studentName: null,
      uploadType: 'general'
    },
    {
      id: 3,
      fileName: 'john_doe_answer_sheet.pdf',
      filePath: '/uploads/john_doe_answer_sheet.pdf',
      fileType: 'application/pdf',
      fileSize: 2048000,
      uploadedAt: '2024-01-16T09:00:00Z',
      studentId: 1,
      studentName: 'John Doe',
      uploadType: 'student'
    },
    {
      id: 4,
      fileName: 'jane_smith_answer_sheet.jpg',
      filePath: '/uploads/jane_smith_answer_sheet.jpg',
      fileType: 'image/jpeg',
      fileSize: 3072000,
      uploadedAt: '2024-01-16T09:15:00Z',
      studentId: 2,
      studentName: 'Jane Smith',
      uploadType: 'student'
    },
  ];

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Replace with API calls
      setTimeout(() => {
        setTest(sampleTest);
        setStudents(sampleStudents);
        setFiles(sampleFiles);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <PdfIcon color="error" />;
    if (fileType?.includes('image')) return <ImageIcon color="primary" />;
    if (fileType?.includes('word') || fileType?.includes('document')) return <DocIcon color="info" />;
    return <FileIcon color="action" />;
  };

  const handleUploadClick = (type) => {
    setUploadType(type);
    setUploadStudent(null);
    setUploadFiles([]);
    setUploadDialogOpen(true);
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = selectedFiles.filter(file => file.size <= 10 * 1024 * 1024); // 10MB limit

    if (validFiles.length !== selectedFiles.length) {
      setSnackbar({
        open: true,
        message: 'Some files exceed the 10MB limit and were not added',
        severity: 'warning'
      });
    }

    setUploadFiles(prev => [...prev, ...validFiles]);
  };

  const handleRemoveUploadFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one file', severity: 'warning' });
      return;
    }

    if (uploadType === 'student' && !uploadStudent) {
      setSnackbar({ open: true, message: 'Please select a student', severity: 'warning' });
      return;
    }

    setUploading(true);
    try {
      // Create FormData for upload
      const formData = new FormData();
      uploadFiles.forEach(file => {
        formData.append('files', file);
      });
      if (uploadType === 'student' && uploadStudent) {
        formData.append('studentId', uploadStudent.studentId);
      }

      // await axios.post(`/api/tests/${id}/files`, formData, {
      //   headers: { 'Content-Type': 'multipart/form-data' }
      // });

      // Simulate upload
      const newFiles = uploadFiles.map((file, index) => ({
        id: Date.now() + index,
        fileName: file.name,
        filePath: `/uploads/${file.name}`,
        fileType: file.type,
        fileSize: file.size,
        uploadedAt: new Date().toISOString(),
        studentId: uploadType === 'student' ? uploadStudent.studentId : null,
        studentName: uploadType === 'student' ? uploadStudent.studentName : null,
        uploadType
      }));

      setTimeout(() => {
        setFiles(prev => [...prev, ...newFiles]);
        setUploading(false);
        setUploadDialogOpen(false);
        setUploadFiles([]);
        setUploadStudent(null);
        setSnackbar({
          open: true,
          message: `${newFiles.length} file(s) uploaded successfully!`,
          severity: 'success'
        });
      }, 1000);
    } catch (error) {
      setUploading(false);
      setSnackbar({ open: true, message: 'Failed to upload files', severity: 'error' });
    }
  };

  const handleDeleteClick = (file) => {
    setSelectedFile(file);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      // await axios.delete(`/api/tests/${id}/files/${selectedFile.id}`);

      setFiles(prev => prev.filter(f => f.id !== selectedFile.id));
      setDeleteDialogOpen(false);
      setSnackbar({ open: true, message: 'File deleted successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete file', severity: 'error' });
    }
  };

  const handleDownload = (file) => {
    // In real implementation, this would trigger a download
    console.log('Downloading:', file.fileName);
    setSnackbar({ open: true, message: `Downloading ${file.fileName}...`, severity: 'info' });
  };

  const handleView = (file) => {
    // In real implementation, this would open a preview
    console.log('Viewing:', file.fileName);
    window.open(file.filePath, '_blank');
  };

  // Filter files
  const generalFiles = files.filter(f => !f.studentId);
  const studentFiles = files.filter(f => f.studentId);

  const filteredFiles = (activeTab === 0 ? generalFiles : studentFiles).filter(file => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      file.fileName.toLowerCase().includes(term) ||
      file.studentName?.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!test) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Test not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/tests/${id}`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              Manage Files
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {test.testName} • {test.className} - {test.sectionName} • {test.subjectName}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => handleUploadClick('general')}
        >
          Upload Files
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <FolderIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
              <Typography color="textSecondary" variant="caption" display="block">
                Total Files
              </Typography>
              <Typography variant="h5">{files.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <FileIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
              <Typography color="textSecondary" variant="caption" display="block">
                Test Files
              </Typography>
              <Typography variant="h5">{generalFiles.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <PersonIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
              <Typography color="textSecondary" variant="caption" display="block">
                Student Files
              </Typography>
              <Typography variant="h5">{studentFiles.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <FileIcon color="action" sx={{ fontSize: 32, mb: 1 }} />
              <Typography color="textSecondary" variant="caption" display="block">
                Total Size
              </Typography>
              <Typography variant="h6">
                {formatFileSize(files.reduce((sum, f) => sum + f.fileSize, 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Upload Options */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              border: '2px dashed',
              borderColor: 'divider',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => handleUploadClick('general')}
          >
            <FolderIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6">Upload Test Files</Typography>
            <Typography variant="body2" color="textSecondary">
              Question papers, answer keys, and other test materials
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              border: '2px dashed',
              borderColor: 'divider',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'success.main',
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => handleUploadClick('student')}
          >
            <PersonIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h6">Upload Student Files</Typography>
            <Typography variant="body2" color="textSecondary">
              Answer sheets and student-specific documents
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs and Search */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
          >
            <Tab label={`Test Files (${generalFiles.length})`} />
            <Tab label={`Student Files (${studentFiles.length})`} />
          </Tabs>
          <TextField
            size="small"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 250 }}
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              }
            }}
          />
        </Box>
      </Paper>

      {/* Files Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell width="60">#</TableCell>
              <TableCell>File Name</TableCell>
              {activeTab === 1 && <TableCell>Student</TableCell>}
              <TableCell>Type</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell align="center" width="150">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={activeTab === 1 ? 7 : 6} align="center" sx={{ py: 4 }}>
                  <FolderIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="textSecondary">
                    No files found
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Click "Upload Files" to add files
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredFiles.map((file, index) => (
                <TableRow key={file.id} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getFileIcon(file.fileType)}
                      <Typography variant="body2">{file.fileName}</Typography>
                    </Box>
                  </TableCell>
                  {activeTab === 1 && (
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 12 }}>
                          {file.studentName?.charAt(0)}
                        </Avatar>
                        <Typography variant="body2">{file.studentName}</Typography>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip
                      label={file.fileType?.split('/')[1]?.toUpperCase() || 'FILE'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatFileSize(file.fileSize)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(file.uploadedAt), 'dd MMM yyyy')}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {format(new Date(file.uploadedAt), 'HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => handleView(file)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => handleDownload(file)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(file)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {uploadType === 'student' ? 'Upload Student Files' : 'Upload Test Files'}
        </DialogTitle>
        <DialogContent>
          {uploadType === 'student' && (
            <Autocomplete
              options={students}
              getOptionLabel={(option) => `${option.rollNo} - ${option.name}`}
              value={uploadStudent}
              onChange={(e, newValue) => setUploadStudent(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Student *"
                  margin="normal"
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                      {option.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Roll No: {option.rollNo}
                      </Typography>
                    </Box>
                  </Box>
                </li>
              )}
            />
          )}

          <Box sx={{ mt: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleFileSelect}
              accept=".pdf"
            />
            <Paper
              sx={{
                p: 3,
                border: '2px dashed',
                borderColor: 'divider',
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1">
                Click to select files
              </Typography>
              <Typography variant="caption" color="textSecondary">
                PDF (max 10MB each)
              </Typography>
            </Paper>
          </Box>

          {uploadFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Files ({uploadFiles.length})
              </Typography>
              <List dense>
                {uploadFiles.map((file, index) => (
                  <ListItem
                    key={index}
                    divider
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveUploadFile(index)}
                        disabled={uploading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemIcon>
                      {getFileIcon(file.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={formatFileSize(file.size)}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Uploading...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUploadDialogOpen(false)}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={handleUpload}
            disabled={uploading || uploadFiles.length === 0 || (uploadType === 'student' && !uploadStudent)}
          >
            {uploading ? 'Uploading...' : `Upload ${uploadFiles.length} File(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{selectedFile?.fileName}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MonthlyTestFiles;